import { Button, Intent, Tooltip } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import LoadingFullScreen from "components/common/LoadingFullScreen";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { useCallback, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
    segmentTrackOpenBulkScenarioCreate,
    segmentTrackUploadBulkScenarioFiles
} from "./analytics";
import css from "./BulkScenarioUploadPage.module.scss";
import { BulkScenarioCreateDialog } from "./components/BulkScenarioUpload/BulkScenarioCreateDialog";
import {
    BulkScenarioDraft,
    bulkScenarioDraftFromCsv,
    BulkScenarioDraftPatch,
    fillBulkScenarioSlots,
    MAX_BULK_SCENARIOS,
    RejectedBulkScenarioFile,
    screenBulkScenarioFiles
} from "./components/BulkScenarioUpload/bulkScenarioDrafts";
import { BulkScenarioSensitivitiesEditor } from "./components/BulkScenarioUpload/BulkScenarioSensitivitiesEditor";
import { BulkScenarioTable } from "./components/BulkScenarioUpload/BulkScenarioTable";
import {
    BulkScenarioUploadLabel,
    createScenariosLabel
} from "./components/BulkScenarioUpload/bulkScenarioUploadLabels";
import { BulkScenarioUploadZone } from "./components/BulkScenarioUpload/BulkScenarioUploadZone";
import { useCreateBulkScenarios } from "./components/BulkScenarioUpload/useCreateBulkScenarios";
import { BackButton } from "./components/common/BackButton";
import { SectionHeader } from "./components/common/SectionHeader";
import { useRequestedScenarioStage } from "./components/ScenarioBuilder/useRequestedScenarioStage";
import {
    CreateScenarioToaster,
    supportedCostAssumptions,
    useIsScenarioBuilderDataReady
} from "./components/ScenarioBuilder/useScenarioBuilderState";
import { stageToDefaultCostAssumption } from "./components/ScenarioDialog/CostAssumptionsPanel";
import {
    parseSensitivitiesCsvFile,
    readSensitivitiesCsv,
    SensitivitiesCsvResult
} from "./components/ScenarioDialog/sensitivitiesCsv";
import { useSensitivitiesCsvRules } from "./components/ScenarioDialog/useSensitivitiesCsvRules";
import { scenarioCount } from "./components/ScenarioRerun/bulkRerunToast";
import { useCluster, useStageConfig } from "./contexts/ClusterContext";
import { useTrackedProject } from "./contexts/ProjectDataContext";
import { useRemainingScenarioSlots } from "./hooks/useIsAtScenarioLimit";
import { useIsBulkScenarioUploadEnabled } from "./hooks/useIsBulkScenarioUploadEnabled";
import { useIsScenarioCreationEnabled } from "./hooks/useIsScenarioCreationEnabled";
import { isOnlyExclusionSupported } from "./types/configPerRegion";
import { TrackedProject } from "./types/projectType";
import { ScenarioStage } from "./types/scenarioType";

const UNREADABLE_FILE: SensitivitiesCsvResult = {
    ok: false,
    headerError: "The file could not be read."
};

export const BulkScenarioUploadPage: React.FC = () => {
    const { projectId } = useParams();
    const cluster = useCluster();
    const isPageEnabled = useIsBulkScenarioUploadEnabled();
    const isDataReady = useIsScenarioBuilderDataReady();
    const scenarioStage = useRequestedScenarioStage();
    const scenarioCreationEnabled = useIsScenarioCreationEnabled({
        stage: scenarioStage
    });
    const project = useTrackedProject({
        projectId,
        phase: cluster.studyPhase
    });

    const projectHref = `/${IN_QUEUE_PATH}/${cluster.slug}/${projectId}`;

    if (!isPageEnabled) {
        return <Navigate to={projectHref} replace />;
    }
    if (project === "loading") {
        return <LoadingFullScreen />;
    }
    if (projectId === undefined || project === undefined) {
        return <Navigate to={`/${IN_QUEUE_PATH}`} replace />;
    }
    if (!isDataReady) {
        return <LoadingFullScreen />;
    }
    if (!scenarioCreationEnabled) {
        return <Navigate to={projectHref} replace />;
    }

    return (
        <BulkScenarioUploadForm
            key={scenarioStage}
            project={project}
            scenarioStage={scenarioStage}
            projectHref={projectHref}
        />
    );
};

const BulkScenarioUploadForm: React.FC<{
    readonly project: TrackedProject;
    readonly scenarioStage: ScenarioStage;
    readonly projectHref: string;
}> = ({ project, scenarioStage, projectHref }) => {
    const navigate = useNavigate();
    const cluster = useCluster();
    const stageConfig = useStageConfig();
    const csvRules = useSensitivitiesCsvRules();
    const remainingScenarioSlots = useRemainingScenarioSlots();
    const { createBulkScenarios, isCreating } = useCreateBulkScenarios({
        project,
        scenarioStage
    });
    const [drafts, setDrafts] = useState<ReadonlyArray<BulkScenarioDraft>>([]);
    const [rejectedFiles, setRejectedFiles] = useState<
        ReadonlyArray<RejectedBulkScenarioFile>
    >([]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    // Dismissed for the whole upload session, so it must outlive the editor.
    const [isErisWarningDismissed, setIsErisWarningDismissed] = useState(false);
    const [editingDraftId, setEditingDraftId] = useState<string>();
    const nextFileId = useRef(0);

    const onlyExclusionSupported = isOnlyExclusionSupported(cluster);
    const defaultCostAssumptions = supportedCostAssumptions(
        stageToDefaultCostAssumption[scenarioStage],
        stageConfig.supportedCostAssumptionTypes[scenarioStage]
    );

    const addFiles = async (files: ReadonlyArray<File>) => {
        if (csvRules === undefined) {
            return;
        }
        const screened = screenBulkScenarioFiles({
            draftFileNames: drafts.map((draft) => draft.fileName),
            files: files.map((file) => ({
                id: String(nextFileId.current++),
                fileName: file.name,
                file
            })),
            maxScenarios: MAX_BULK_SCENARIOS
        });
        const readFiles = await Promise.all(
            screened.toRead.map(async ({ id, fileName, file }) => ({
                id,
                fileName,
                result: await parseSensitivitiesCsvFile(file).then(
                    (results) => readSensitivitiesCsv(results, csvRules),
                    () => UNREADABLE_FILE
                )
            }))
        );
        const filled = fillBulkScenarioSlots({
            draftCount: drafts.length,
            files: readFiles,
            maxScenarios: MAX_BULK_SCENARIOS
        });
        const newDrafts = filled.accepted.map(({ id, fileName, csv }) =>
            bulkScenarioDraftFromCsv({
                id,
                fileName,
                csv,
                costAssumptions: defaultCostAssumptions
            })
        );
        const newRejectedFiles = [
            ...screened.rejectedFiles,
            ...filled.rejectedFiles
        ];

        segmentTrackUploadBulkScenarioFiles({
            cluster,
            projectId: project.projectId,
            numFiles: files.length,
            numRejectedFiles: newRejectedFiles.length
        });
        setDrafts((previous) => [...previous, ...newDrafts]);
        setRejectedFiles((previous) => [...previous, ...newRejectedFiles]);
    };

    const removeDraft = (id: string) =>
        setDrafts((previous) => previous.filter((draft) => draft.id !== id));

    const updateDrafts = useCallback(
        (ids: ReadonlySet<string>, patch: BulkScenarioDraftPatch) =>
            setDrafts((previous) =>
                previous.map((draft) =>
                    ids.has(draft.id)
                        ? {
                              ...draft,
                              ...(typeof patch === "function"
                                  ? patch(draft)
                                  : patch)
                          }
                        : draft
                )
            ),
        []
    );

    const hasDrafts = drafts.length > 0;
    const editingDraft = drafts.find((draft) => draft.id === editingDraftId);
    const overLimitCount =
        remainingScenarioSlots === undefined
            ? 0
            : Math.max(0, drafts.length - remainingScenarioSlots);

    const createBlockedReason = !hasDrafts
        ? "Add at least one readable CSV"
        : drafts.some((draft) => draft.title.trim() === "")
        ? "Every scenario needs a title"
        : undefined;

    const openCreateDialog = () => {
        segmentTrackOpenBulkScenarioCreate({
            projectId: project.projectId,
            numScenarios: drafts.length
        });
        setIsCreateDialogOpen(true);
    };

    const onCreate = async () => {
        const createdCount = await createBulkScenarios(drafts);
        if (createdCount === 0) {
            CreateScenarioToaster.show({
                intent: Intent.DANGER,
                message: "The scenarios were not created. Please try again."
            });
            return;
        }
        setIsCreateDialogOpen(false);
        CreateScenarioToaster.show({
            intent: Intent.SUCCESS,
            message: `${scenarioCount(createdCount)} created`
        });
        navigate(projectHref);
    };

    return (
        <>
            <BackButton
                text={`Back to Project ${project.projectId}`}
                to={projectHref}
            />
            <div className={css["BulkScenarioUploadPage"]}>
                <SectionHeader
                    level="h1"
                    title={BulkScenarioUploadLabel.PageTitle}
                />
                <div className={css["BulkScenarioUploadPage-panel"]}>
                    <BulkScenarioUploadZone
                        projectId={project.projectId}
                        rejectedFiles={rejectedFiles}
                        onlyExclusionSupported={onlyExclusionSupported}
                        onFilesSelected={addFiles}
                        onReject={(message) =>
                            CreateScenarioToaster.show({
                                intent: Intent.WARNING,
                                message
                            })
                        }
                        onRemoveRejectedFile={(id) =>
                            setRejectedFiles((previous) =>
                                previous.filter((file) => file.id !== id)
                            )
                        }
                    />
                    {hasDrafts && (
                        <BulkScenarioTable
                            drafts={drafts}
                            overLimitCount={overLimitCount}
                            onUpdateDrafts={updateDrafts}
                            onRemoveDraft={removeDraft}
                            onOpenDraft={setEditingDraftId}
                            scenarioStage={scenarioStage}
                            defaultCostAssumptions={defaultCostAssumptions}
                            projectId={project.projectId}
                            onlyExclusionSupported={onlyExclusionSupported}
                        />
                    )}
                </div>
                <div className={css["BulkScenarioUploadPage-footer"]}>
                    <Button
                        className={css["BulkScenarioUploadPage-footer-close"]}
                        text="Close"
                        minimal
                        onClick={() => navigate(projectHref)}
                    />
                    <Tooltip
                        content={createBlockedReason}
                        disabled={createBlockedReason === undefined}
                    >
                        <Button
                            text={createScenariosLabel(drafts.length)}
                            intent={Intent.PRIMARY}
                            icon={IconNames.ADD}
                            disabled={createBlockedReason !== undefined}
                            onClick={openCreateDialog}
                        />
                    </Tooltip>
                </div>
            </div>
            {isCreateDialogOpen && (
                <BulkScenarioCreateDialog
                    drafts={drafts}
                    defaultCostAssumptions={defaultCostAssumptions}
                    remainingScenarioSlots={remainingScenarioSlots}
                    overLimitCount={overLimitCount}
                    isCreating={isCreating}
                    onClose={() => setIsCreateDialogOpen(false)}
                    onCreate={onCreate}
                />
            )}
            {editingDraft !== undefined && (
                <BulkScenarioSensitivitiesEditor
                    draft={editingDraft}
                    onlyExclusionSupported={onlyExclusionSupported}
                    onUpdateAssumptions={(patch) =>
                        updateDrafts(new Set([editingDraft.id]), patch)
                    }
                    isErisWarningDismissed={isErisWarningDismissed}
                    setIsErisWarningDismissed={setIsErisWarningDismissed}
                    onClose={() => setEditingDraftId(undefined)}
                />
            )}
        </>
    );
};
